import React, { createContext, useContext, useEffect, useState } from 'react';
import { COUNTRY_TO_LOCALE, DEFAULT_LOCALE, LOCALES } from '../i18n';
import { isPrerendering } from '../utils/prerender';

const LocaleContext = createContext(null);

const MANUAL_KEY = 'smartly_locale_manual'; // set only when user picks manually

const PROXY_BASE = process.env.REACT_APP_PROXY_URL || 'http://localhost:3001';

const GEO_SESSION_KEY = 'smartly_geo_locale';

async function detectLocaleFromIp() {
  // Cache sessionStorage — un seul appel par session navigateur
  const cached = sessionStorage.getItem(GEO_SESSION_KEY);
  if (cached) return cached;

  try {
    // Appel via notre backend proxy — l'IP du visiteur ne part jamais vers des tiers
    const r = await fetch(`${PROXY_BASE}/proxy/geoip`, { signal: AbortSignal.timeout(5000) });
    if (r.ok) {
      const data = await r.json();
      if (data.country_code) {
        const locale = COUNTRY_TO_LOCALE[data.country_code] ?? 'world-en';
        sessionStorage.setItem(GEO_SESSION_KEY, locale);
        return locale;
      }
    }
  } catch {}
  return DEFAULT_LOCALE;
}

export function LocaleProvider({ children }) {
  const [localeKey, setLocaleKey] = useState(DEFAULT_LOCALE);
  const [detecting, setDetecting] = useState(true);

  useEffect(() => {
    if (isPrerendering()) {
      // Le HTML statique doit rester sur DEFAULT_LOCALE (l'état initial exact que le
      // client reproduit avant ses propres effets) — sinon l'hydration ne correspond
      // jamais au contenu géolocalisé figé dans le snapshot.
      setDetecting(false);
      return;
    }
    const manual = localStorage.getItem(MANUAL_KEY);
    if (manual) {
      // User explicitly chose — respect it regardless of IP
      setLocaleKey(manual);
      applyDir(manual);
      setDetecting(false);
    } else {
      detectLocaleFromIp().then((detected) => {
        setLocaleKey(detected);
        applyDir(detected);
        setDetecting(false);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applyDir(key) {
    const meta = LOCALES.find(l => l.key === key);
    document.documentElement.dir = meta?.dir ?? 'ltr';
    document.documentElement.lang = meta ? `${meta.country.toLowerCase()}-${key.split('-')[1]}` : 'fr';
  }

  function changeLocale(key) {
    setLocaleKey(key);
    localStorage.setItem(MANUAL_KEY, key);
    applyDir(key);
  }

  const currentMeta = LOCALES.find(l => l.key === localeKey) ?? LOCALES[0];

  return (
    <LocaleContext.Provider value={{ localeKey, currentMeta, changeLocale, detecting }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
